import { useState } from 'react';
import { Play } from 'lucide-react';
import { PageHeader } from '../../components/ui/States';
import { Badge } from '../../components/ui/Badge';

interface Endpoint {
  id: string;
  label: string;
  method: 'GET' | 'POST';
  path: string;
  scope: string;
  /** Names of path/query/body inputs the caller must or may fill in. */
  inputs: { name: string; label: string; required?: boolean; where: 'path' | 'query' | 'body' }[];
}

const HOME: Endpoint = {
  id: 'home',
  label: 'Home screen',
  method: 'GET',
  path: '/v1/home',
  scope: 'catalog:read',
  inputs: [],
};

const ENDPOINTS: Endpoint[] = [
  HOME,
  { id: 'feed', label: 'For You feed', method: 'GET', path: '/v1/feed/foryou', scope: 'catalog:read', inputs: [] },
  { id: 'cats', label: 'Categories', method: 'GET', path: '/v1/categories', scope: 'catalog:read', inputs: [] },
  {
    id: 'titles',
    label: 'List or search titles',
    method: 'GET',
    path: '/v1/titles',
    scope: 'catalog:read',
    inputs: [
      { name: 'q', label: 'Search text', where: 'query' },
      { name: 'genre', label: 'Genre', where: 'query' },
      { name: 'limit', label: 'How many (1 to 50)', where: 'query' },
    ],
  },
  {
    id: 'title',
    label: 'One title',
    method: 'GET',
    path: '/v1/titles/:slug',
    scope: 'catalog:read',
    inputs: [{ name: 'slug', label: 'Title slug', required: true, where: 'path' }],
  },
  {
    id: 'episodes',
    label: 'Episodes of a title',
    method: 'GET',
    path: '/v1/titles/:slug/episodes',
    scope: 'catalog:read',
    inputs: [{ name: 'slug', label: 'Title slug', required: true, where: 'path' }],
  },
  {
    id: 'similar',
    label: 'Similar titles',
    method: 'GET',
    path: '/v1/titles/:slug/similar',
    scope: 'catalog:read',
    inputs: [{ name: 'slug', label: 'Title slug', required: true, where: 'path' }],
  },
  {
    id: 'playback',
    label: 'Get a video link',
    method: 'POST',
    path: '/v1/playback',
    scope: 'playback:mint',
    inputs: [
      { name: 'titleId', label: 'Title ID', required: true, where: 'body' },
      { name: 'episodeId', label: 'Episode ID', required: true, where: 'body' },
    ],
  },
];

interface Result {
  status: number;
  statusText: string;
  ms: number;
  body: string;
}

export function Playground() {
  const [apiKey, setApiKey] = useState('');
  const [endpointId, setEndpointId] = useState('home');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const endpoint: Endpoint = ENDPOINTS.find((e) => e.id === endpointId) ?? HOME;
  const missing = endpoint.inputs.filter((i) => i.required && !values[i.name]?.trim());
  const canSend = apiKey.trim() !== '' && missing.length === 0 && !busy;

  async function send() {
    const base = (import.meta.env.VITE_API_BASE_URL ?? window.location.origin) as string;
    let path = endpoint.path;
    const query = new URLSearchParams();
    const body: Record<string, string> = {};
    for (const i of endpoint.inputs) {
      const v = values[i.name]?.trim();
      if (!v) continue;
      if (i.where === 'path') path = path.replace(`:${i.name}`, encodeURIComponent(v));
      else if (i.where === 'query') query.set(i.name, v);
      else body[i.name] = v;
    }
    const qs = query.toString();
    const url = `${base.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`;

    setBusy(true);
    setResult(null);
    setFailure(null);
    const started = performance.now();
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${apiKey.trim()}` };
      const init: RequestInit = { method: endpoint.method, headers };
      if (endpoint.method === 'POST') {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      const res = await fetch(url, init);
      const text = await res.text();
      const ms = Math.round(performance.now() - started);
      let pretty = text;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        /* not JSON: show as received */
      }
      setResult({ status: res.status, statusText: res.statusText, ms, body: pretty || '(empty response)' });
    } catch {
      setFailure('The request did not reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  const ok = result ? result.status >= 200 && result.status < 300 : false;

  return (
    <div>
      <PageHeader
        title="Playground"
        subtitle="Try a call with one of your API keys and see exactly what comes back."
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-[#120d1a] border border-[#241b33] rounded-xl p-5 space-y-4">
          <label className="block">
            <span className="text-xs text-gray-400">Your API key</span>
            <input
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="dk_live_…"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0b0712] border border-[#241b33] text-sm text-white font-mono"
            />
            <span className="block text-xs text-gray-500 mt-1">
              The key is only used for this test and is not saved.
            </span>
          </label>

          <label className="block">
            <span className="text-xs text-gray-400">What do you want to try?</span>
            <select
              value={endpointId}
              onChange={(e) => {
                setEndpointId(e.target.value);
                setValues({});
                setResult(null);
                setFailure(null);
              }}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0b0712] border border-[#241b33] text-sm text-white"
            >
              {ENDPOINTS.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.label}
                </option>
              ))}
            </select>
            <span className="block text-xs text-gray-500 mt-1">
              <span className="font-mono">
                {endpoint.method} {endpoint.path}
              </span>
              {' · needs '}
              <span className="font-mono">{endpoint.scope}</span>
            </span>
          </label>

          {endpoint.inputs.map((i) => (
            <label key={i.name} className="block">
              <span className="text-xs text-gray-400">
                {i.label}
                {i.required ? '' : ' (optional)'}
              </span>
              <input
                value={values[i.name] ?? ''}
                onChange={(e) => setValues((cur) => ({ ...cur, [i.name]: e.target.value }))}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0b0712] border border-[#241b33] text-sm text-white"
              />
            </label>
          ))}

          <button
            onClick={send}
            disabled={!canSend}
            title={
              apiKey.trim() === ''
                ? 'Paste an API key first'
                : missing.length > 0
                  ? `Fill in: ${missing.map((m) => m.label).join(', ')}`
                  : undefined
            }
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-xs font-semibold text-white"
          >
            <Play className="w-4 h-4" /> {busy ? 'Sending…' : 'Send request'}
          </button>
        </div>

        <div className="bg-[#120d1a] border border-[#241b33] rounded-xl p-5 min-h-[12rem]">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-3">Response</div>
          {busy && <p className="text-sm text-gray-400">Waiting for the server…</p>}
          {failure && (
            <p className="text-sm text-rose-400" role="alert">
              {failure}
            </p>
          )}
          {!busy && !failure && !result && (
            <p className="text-sm text-gray-500">Nothing sent yet. The answer will appear here.</p>
          )}
          {result && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={ok ? 'success' : 'danger'}>
                  {result.status} {result.statusText || (ok ? 'OK' : 'Error')}
                </Badge>
                <span className="text-xs text-gray-400">{result.ms} ms</span>
              </div>
              <pre className="text-xs text-gray-200 bg-[#0b0712] border border-[#241b33] rounded-lg p-3 overflow-auto max-h-[32rem] whitespace-pre-wrap break-words">
                {result.body}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
